import {
  fetchTokenResult,
  type TokenResult,
} from "@bindery/auth-consumer/browser";
import type { ConvexReactClient } from "convex/react";
import { useSyncExternalStore } from "react";
import {
  SESSION_PATH,
  type SessionAnswer,
  sessionAnswerSchema,
} from "@/contracts/session";
import { renewReaderSession } from "@/auth/renew";
import { rememberLastReader } from "@/storage/last-reader";
import { isUnconfirmed, landingGate } from "@/storage/unconfirmed-reader";

// readerId comes from here only, never the URL or a Convex argument: the
// Worker read it from a verified token. `receivedAt` pairs with
// `session.serverTime` for clock-skew estimates.
export type SessionState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "ready"; session: SessionAnswer; receivedAt: number }
  | { status: "unavailable" };

export interface SessionStore {
  readonly getState: () => SessionState;
  readonly subscribe: (listener: () => void) => () => void;
  readonly refresh: () => Promise<void>;
}

const LOADING: SessionState = { status: "loading" };

// A scanned reader nobody has confirmed is not yet the device's reader;
// BookmarkIdentityGate remembers it on "That's me". The landing URL counts
// as well as the stored marker: this answer can land before RequireReader
// has moved the gate from one to the other.
function rememberConfirmedReader(readerId: string): void {
  const scanned =
    isUnconfirmed(readerId) || landingGate(location.search) === readerId;
  if (!scanned) rememberLastReader(readerId);
}

// The session cookie is `/auth`-scoped, so it never reaches `/api/session`:
// the Worker's gate reads only a Bearer token, fetched from `/auth/token`.
// Only the Worker saying "no session" signs the page out; a token that
// could not be fetched is unavailable, and retried.
export function createSessionStore({
  fetchToken = fetchTokenResult,
  fetchSession = (input: string, init: RequestInit) => fetch(input, init),
  rememberReader = rememberConfirmedReader,
  renewReader = renewReaderSession,
  now = Date.now,
}: {
  renewReader?: () => Promise<void>;
  fetchToken?: () => Promise<TokenResult>;
  fetchSession?: (input: string, init: RequestInit) => Promise<Response>;
  rememberReader?: (readerId: string) => void;
  now?: () => number;
} = {}): SessionStore {
  let state: SessionState = LOADING;
  let started = false;
  let latestRequest = 0;
  let renewalAsked = false;
  const listeners = new Set<() => void>();

  function publish(next: SessionState): void {
    state = next;
    if (next.status === "ready" && next.session.lane === "reader_session") {
      rememberReader(next.session.readerId);
      if (!renewalAsked) {
        renewalAsked = true;
        void renewReader();
      }
    }
    for (const listener of listeners) listener();
  }

  async function read(): Promise<SessionState> {
    try {
      const token = await fetchToken();
      if (token.kind === "signed-out") return { status: "signed-out" };
      if (token.kind === "unreachable") return { status: "unavailable" };
      const response = await fetchSession(SESSION_PATH, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { authorization: `Bearer ${token.token}` },
      });
      if (response.status === 401) return { status: "signed-out" };
      if (!response.ok) return { status: "unavailable" };
      const parsed = sessionAnswerSchema.safeParse(await response.json());
      if (!parsed.success) return { status: "unavailable" };
      return { status: "ready", session: parsed.data, receivedAt: now() };
    } catch {
      return { status: "unavailable" };
    }
  }

  async function refresh(): Promise<void> {
    started = true;
    latestRequest += 1;
    const request = latestRequest;
    const next = await read();
    if (request === latestRequest) publish(next);
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      if (!started) void refresh();
      return () => {
        listeners.delete(listener);
      };
    },
    refresh,
  };
}

export const sessionStore = createSessionStore();

// setAuth's onChange fires once at boot and again on every token refresh;
// only a flip between signed in and out means the session answer changed,
// and the boot answer is already in flight from the first subscriber.
export function refreshOnAuthFlip(
  refresh: () => Promise<void>,
): (isAuthenticated: boolean) => void {
  let last: boolean | undefined;
  return (isAuthenticated) => {
    if (last !== undefined && last !== isAuthenticated) void refresh();
    last = isAuthenticated;
  };
}

export const SESSION_RETRY_BASE_MS = 1_000;
export const SESSION_RETRY_MAX_MS = 60_000;

export interface SessionRecoveryDeps {
  store: SessionStore;
  convex?: Pick<ConvexReactClient, "subscribeToConnectionState">;
  target?: Pick<Window, "addEventListener" | "removeEventListener">;
  doc?: Pick<Document, "addEventListener" | "removeEventListener"> & {
    readonly visibilityState: DocumentVisibilityState;
  };
}

/**
 * Nothing else re-reads a session that failed at boot, so while it is
 * unavailable this retries on a doubling timer and at once on `online`,
 * a Convex reconnect or the page coming back into view. Returns the
 * teardown.
 */
export function recoverUnavailableSession({
  store,
  convex,
  target = window,
  doc = document,
}: SessionRecoveryDeps): () => void {
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const unavailable = () => store.getState().status === "unavailable";
  const retryIfUnavailable = () => {
    if (unavailable()) void store.refresh();
  };

  const stopStore = store.subscribe(() => {
    clearTimeout(timer);
    if (!unavailable()) {
      failures = 0;
      return;
    }
    const delay = Math.min(
      SESSION_RETRY_BASE_MS * 2 ** failures,
      SESSION_RETRY_MAX_MS,
    );
    failures += 1;
    timer = setTimeout(retryIfUnavailable, delay);
  });

  const onVisibility = () => {
    if (doc.visibilityState === "visible") retryIfUnavailable();
  };
  target.addEventListener("online", retryIfUnavailable);
  doc.addEventListener("visibilitychange", onVisibility);

  let connected = true;
  const stopConnection = convex?.subscribeToConnectionState((state) => {
    if (state.isWebSocketConnected && !connected) retryIfUnavailable();
    connected = state.isWebSocketConnected;
  });

  return () => {
    clearTimeout(timer);
    stopStore();
    target.removeEventListener("online", retryIfUnavailable);
    doc.removeEventListener("visibilitychange", onVisibility);
    stopConnection?.();
  };
}

export function useSession(store: SessionStore = sessionStore): SessionState {
  return useSyncExternalStore(store.subscribe, store.getState, () => LOADING);
}

export function readerIdOf(state: SessionState): string | null {
  return state.status === "ready" ? state.session.readerId : null;
}

export function needsReaderSelection(state: SessionState): boolean {
  return (
    state.status === "ready" &&
    state.session.lane === "user_session" &&
    state.session.readerId === null
  );
}
